import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { QUERY_TATTOOS_ENDPOINT } from '@/config/endpoints'
import { useSimilarItems } from '@/hooks/useSimilarItems'
import { useArtists } from '@/hooks/useArtists'
import { useAuthState } from '@/hooks/useAuth'
import { useUserData } from '@/hooks/useUserData'
import { FlashCard } from '@/components/shared/tattoo/FlashCard'
import { TattooCard } from '@/components/shared/tattoo/TattooCard'
import { BaseMasonryGrid } from '@/components/shared/BaseMasonryGrid'
import type { PortfolioItem, Artist } from '@/types/models'
import { getUniqueArtists } from '@/lib/tattooData'

const FALLBACK_MAX_ITEMS = 32

async function fetchPortfolioDocByRef(
  itemDocId: string,
  docItemType: 'flash' | 'portfolioItem' | 'healedPortfolioItem'
): Promise<PortfolioItem | null> {
  try {
    let collectionName =
      docItemType === 'flash'
        ? 'flashes'
        : docItemType === 'healedPortfolioItem'
          ? 'healedPortfolioItems'
          : 'portfolioItems'
    let docRef = doc(db, collectionName, itemDocId)
    let docSnap = await getDoc(docRef)

    if (!docSnap.exists() && docItemType === 'flash') {
      collectionName = 'flashesArchived'
      docRef = doc(db, collectionName, itemDocId)
      docSnap = await getDoc(docRef)
    }

    if (!docSnap.exists()) {
      return null
    }

    const data = docSnap.data()

    if (docItemType === 'flash' && data.status === 'artistDeleted') {
      return null
    }

    const item: PortfolioItem = {
      id: docSnap.id,
      artistId: data.artistId as string,
      type: (data.type as PortfolioItem['type']) ?? 'image',
      storagePath: (data.storagePath as string) ?? '',
      imageUrl: data.imageUrl as string,
      width: (data.width as number) ?? 0,
      height: (data.height as number) ?? 0,
      createdAt: data.createdAt?.toDate?.() ?? new Date(),
    }

    if (data.compressedImageUrl) item.compressedImageUrl = data.compressedImageUrl
    if (data.styles) item.styleTags = data.styles as string[]
    if (data.caption) item.caption = data.caption as string
    if (data.timedSaves) {
      item.timedSaves = data.timedSaves.map((saveData: any) => ({
        itemId: saveData.itemId,
        userId: saveData.userId,
        savedAt: saveData.savedAt?.toDate?.() || new Date(saveData.savedAt),
      }))
    }

    if (docItemType === 'flash') {
      item.isFlash = true
      if (data.minSize) item.minSize = data.minSize as string
      if (data.minPrice) item.minPrice = data.minPrice as string
      if (data.booked !== undefined) item.booked = data.booked as boolean
      if (data.accessCode) item.accessCode = data.accessCode as string
      if (data.reservationHistory) item.reservationHistory = data.reservationHistory
      if (data.discount) {
        item.discount = {
          absoluteAmount: data.discount.absoluteAmount,
          procentualAccount: data.discount.procentualAccount,
          newPrice: data.discount.newPrice,
          oldPrice: data.discount.oldPrice,
          discountStartsAt:
            data.discount.discountStartsAt?.toDate?.() ||
            new Date(data.discount.discountStartsAt),
          discountEndsAt:
            data.discount.discountEndsAt?.toDate?.() ||
            new Date(data.discount.discountEndsAt),
        }
      }
    }

    if (docItemType === 'healedPortfolioItem') {
      item.isHealed = true
    }

    return item
  } catch (err) {
    console.error(`[SimilarItems] Error fetching item ${itemDocId}:`, err)
    return null
  }
}

async function hydrateItemRefColumns(
  leftItems: ReadonlyArray<{ id: string; type: 'flash' | 'portfolioItem' | 'healedPortfolioItem' }>,
  rightItems: ReadonlyArray<{ id: string; type: 'flash' | 'portfolioItem' | 'healedPortfolioItem' }>,
  excludeItemId: string | undefined
): Promise<PortfolioItem[]> {
  const [leftResults, rightResults] = await Promise.all([
    Promise.all(leftItems.map((item) => fetchPortfolioDocByRef(item.id, item.type))),
    Promise.all(rightItems.map((item) => fetchPortfolioDocByRef(item.id, item.type))),
  ])

  const merged = [...leftResults, ...rightResults].filter(Boolean) as PortfolioItem[]
  const excluded = excludeItemId ? merged.filter((i) => i.id !== excludeItemId) : merged

  const seen = new Set<string>()
  const deduped: PortfolioItem[] = []
  for (const item of excluded) {
    if (seen.has(item.id)) continue
    seen.add(item.id)
    deduped.push(item)
  }
  return deduped
}

function collectionsForFallbackItemType(
  itemType: 'flash' | 'portfolioItem' | 'healedPortfolioItem'
): ('flashes' | 'portfolioItems' | 'healedPortfolioItems')[] {
  switch (itemType) {
    case 'flash':
      return ['flashes']
    case 'healedPortfolioItem':
      return ['healedPortfolioItems']
    default:
      return ['portfolioItems']
  }
}

interface TattooItemRef {
  type: 'flash' | 'portfolioItem' | 'healedPortfolioItem'
  id: string
}

interface QueryTattoosResponse {
  left: TattooItemRef[]
  right: TattooItemRef[]
}

interface SimilarItemsProps {
  itemId: string
  itemType: 'flash' | 'portfolioItem' | 'healedPortfolioItem'
  /** When similar-items fails or returns nothing hydrated, loads more work from this artist (query-tattoos). */
  fallbackArtistId?: string
}

export function SimilarItems({
  itemId,
  itemType,
  fallbackArtistId,
}: SimilarItemsProps) {
  const { t } = useTranslation()
  const { user, loading: authLoading } = useAuthState()
  const { isAdminModeActive, loading: userDataLoading } = useUserData()
  const userDataReady = !authLoading && (!user || !userDataLoading)
  const { leftItems, rightItems, loading: loadingSimilarIds, error } = useSimilarItems(itemId, itemType)
  const [itemsData, setItemsData] = useState<PortfolioItem[]>([])
  const [listingSource, setListingSource] = useState<'similar' | 'fallback'>('similar')
  const [loadingItems, setLoadingItems] = useState(false)
  const [fallbackLoading, setFallbackLoading] = useState(false)

  const fallbackAttemptKeyRef = useRef<string | null>(null)
  const fallbackBusyRef = useRef(false)

  useEffect(() => {
    setItemsData([])
    setListingSource('similar')
    setLoadingItems(false)
    setFallbackLoading(false)
    fallbackAttemptKeyRef.current = null
    fallbackBusyRef.current = false
  }, [itemId, itemType, fallbackArtistId])

  useEffect(() => {
    if (loadingSimilarIds) return

    if (error) {
      setItemsData([])
      return
    }

    if (!leftItems.length && !rightItems.length) {
      setItemsData([])
      return
    }

    const fetchItems = async () => {
      setLoadingItems(true)
      try {
        const hydrated = await hydrateItemRefColumns(leftItems, rightItems, itemId)
        setItemsData(hydrated)
        if (hydrated.length > 0) {
          setListingSource('similar')
        }
      } catch (err) {
        console.error('[SimilarItems] Error hydrating similar items:', err)
      } finally {
        setLoadingItems(false)
      }
    }

    void fetchItems()
  }, [error, leftItems, rightItems, loadingSimilarIds, itemId])

  useEffect(() => {
    if (!fallbackArtistId || !userDataReady) return

    const primaryIdle = !loadingSimilarIds && !loadingItems

    if (!primaryIdle || itemsData.length > 0 || fallbackBusyRef.current) {
      return
    }

    const attemptKey = `${itemId}:${itemType}:${fallbackArtistId}`
    if (fallbackAttemptKeyRef.current === attemptKey) {
      return
    }

    const ac = new AbortController()

    const loadFallbackFromArtist = async () => {
      fallbackBusyRef.current = true
      setFallbackLoading(true)
      try {
        const collections = collectionsForFallbackItemType(itemType)
        const response = await fetch(QUERY_TATTOOS_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: ac.signal,
          body: JSON.stringify({
            collections,
            sorting: 'recent',
            saved: false,
            userId: user?.uid || null,
            cities: undefined,
            artistId: fallbackArtistId,
            style: undefined,
            includeBooked: false,
            deals: false,
            isAdmin: isAdminModeActive,
          }),
        })

        if (!response.ok) {
          throw new Error(`query-tattoos ${response.status}`)
        }

        const data: QueryTattoosResponse = await response.json()
        const left = data.left ?? []
        const right = data.right ?? []

        let hydrated =
          left.length === 0 && right.length === 0
            ? []
            : await hydrateItemRefColumns(left, right, itemId)

        hydrated = hydrated.slice(0, FALLBACK_MAX_ITEMS)

        setItemsData(hydrated)
        if (hydrated.length > 0) {
          setListingSource('fallback')
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          console.error('[SimilarItems] Fallback artist items failed:', err)
        }
      } finally {
        fallbackBusyRef.current = false
        setFallbackLoading(false)
        if (!ac.signal.aborted) {
          fallbackAttemptKeyRef.current = attemptKey
        }
      }
    }

    void loadFallbackFromArtist()

    return () => {
      ac.abort()
    }
  }, [
    fallbackArtistId,
    isAdminModeActive,
    itemId,
    itemType,
    itemsData.length,
    loadingItems,
    loadingSimilarIds,
    user?.uid,
    userDataReady,
  ])

  const allItemsData = useMemo(() => itemsData, [itemsData])

  const uniqueArtistIds = useMemo(() => {
    const ids = getUniqueArtists(allItemsData)
    if (
      fallbackArtistId &&
      listingSource === 'fallback' &&
      ids.indexOf(fallbackArtistId) === -1
    ) {
      return [...ids, fallbackArtistId]
    }
    return ids
  }, [allItemsData, fallbackArtistId, listingSource])

  const { artists } = useArtists(uniqueArtistIds)

  const headingArtistRecord = useMemo(() => {
    if (!fallbackArtistId || listingSource !== 'fallback') return undefined
    return artists.find((a) => a.id === fallbackArtistId)
  }, [fallbackArtistId, listingSource, artists])

  const moreFromTitle = useMemo(() => {
    const artistName = headingArtistRecord?.displayName ?? t('common.artistName')
    return t('common.moreFromArtist', { artistName })
  }, [headingArtistRecord, t])

  const renderCard = useCallback((item: PortfolioItem, artist?: Artist) => {
    if (item.isFlash) {
      return <FlashCard item={item} artist={artist} preloaded={false} />
    }
    return <TattooCard item={item} artist={artist} preloaded={false} />
  }, [])

  const handleLoadMoreItems = useCallback(async () => {
    /* similar section has no pagination */
  }, [])

  const loadingCombined = loadingSimilarIds || loadingItems || fallbackLoading

  if (!itemsData.length && !loadingCombined) {
    return null
  }

  const sectionTitle =
    listingSource === 'fallback'
      ? moreFromTitle
      : itemType === 'flash'
        ? t('common.similarFlashes')
        : t('common.similarTattoos')

  return (
    <div className="mb-8">
      <h2 className="text-xl font-semibold mb-4">{sectionTitle}</h2>

      <BaseMasonryGrid
        items={itemsData}
        loading={loadingCombined}
        loadingMore={false}
        hasMore={false}
        error={undefined}
        loadMoreItems={handleLoadMoreItems}
        renderItem={renderCard}
        artists={artists}
        artistsLoading={false}
        initialLoadComplete={!loadingCombined}
        reloadOnScroll={false}
      />
    </div>
  )
}
